/**
 * Offline identity cache for `GET /api/v1/me` (barn dead-zone cold start).
 *
 * `hooks/useMe.ts` writes the last successful `/me` here and, on a cold
 * start where `/me` cannot be reached at all (a network failure, never a
 * 401/403), resolves to this cached identity flagged `offline` so the app
 * shell, the service-worker-cached Today list, and the offline banner
 * render instead of the full-screen "Couldn't connect" page.
 *
 * Deliberately minimal: only what the shell needs to render (id, display
 * name, username, role-derived gates, passkey flag). Never a session
 * token, never a password, nothing the server would not already show this
 * same device's signed-in person. `localStorage` is per-origin by spec, so
 * the cache never leaks across deployments on different hosts.
 *
 * Cleared on: logout (`useMe().logout`), any 401 from our own API
 * (`lib/api.ts`'s `request()`), and a 401/403 on `/me` itself. A session
 * that still owes an onboarding/reset step is never cached, so an offline
 * cold start can never skip past those forced screens.
 */

const STORAGE_KEY = "hayloft.me.v1";

export interface CachedIdentity {
  id: string;
  name: string;
  username: string | null;
  canHandOutWork: boolean;
  isAdmin: boolean;
  hasPasskey: boolean;
  /** Epoch ms of the `/me` response this was written from. */
  savedAt: number;
}

function storage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    // Some privacy modes throw on mere access.
    return null;
  }
}

export function readCachedIdentity(): CachedIdentity | null {
  const store = storage();
  if (!store) return null;
  try {
    const raw = store.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CachedIdentity>;
    if (typeof parsed.id !== "string" || typeof parsed.name !== "string") return null;
    return {
      id: parsed.id,
      name: parsed.name,
      username: typeof parsed.username === "string" ? parsed.username : null,
      canHandOutWork: parsed.canHandOutWork === true,
      isAdmin: parsed.isAdmin === true,
      hasPasskey: parsed.hasPasskey === true,
      savedAt: typeof parsed.savedAt === "number" ? parsed.savedAt : 0,
    };
  } catch {
    return null;
  }
}

export function writeCachedIdentity(identity: Omit<CachedIdentity, "savedAt">): void {
  const store = storage();
  if (!store) return;
  try {
    store.setItem(STORAGE_KEY, JSON.stringify({ ...identity, savedAt: Date.now() }));
  } catch {
    // Quota/disabled storage: the app still works online, it just can't
    // open offline on the next cold start.
  }
}

export function clearCachedIdentity(): void {
  const store = storage();
  if (!store) return;
  try {
    store.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clear if storage is unavailable.
  }
}
